import apiClient from './client';
import type { AbsentReason } from '../../types';

// ============================================================================
// Types
// ============================================================================

export type TimelineEventCategory = 'coach_event' | 'group_session' | 'program_milestone';

export interface TimelineEvent {
  id: string;
  name: string;
  category: TimelineEventCategory;
  date_display: string;
  scheduled_start: string | null;
  month_key: string;
  month_label: string;
}

export interface CohortTimelineResponse {
  cohort_id: string;
  cohort_name: string;
  total_events: number;
  events: TimelineEvent[];
}

export type CohortEventType =
  | 'coach_immersion'
  | 'participant_orientation'
  | 'coach_huddle'
  | 'cohort_wrap_up'
  | 'in_person_booster';

export type CohortEventStatus =
  | 'not_scheduled'
  | 'ready'
  | 'scheduled'
  | 'awaiting_attendance'
  | 'completed';

export type EventRecipientType = 'coach' | 'participant' | 'external';

export interface CohortEventRsvpSummary {
  total: number;
  accepted: number;
  declined: number;
  pending: number;
  tentative: number;
}

export interface CohortEventWithDetails {
  id: string;
  cohort_id: string;
  event_type: CohortEventType;
  event_name: string;
  event_number: number;
  scheduled_start: string | null;
  scheduled_end: string | null;
  timezone: string;
  video_link: string | null;
  google_calendar_event_id: string | null;
  invites_sent_at: string | null;
  attendance_marked: boolean;
  status: CohortEventStatus;
  created_at: string;
  updated_at: string;
  cohort_name?: string;
  cohort_code?: string;
  default_meeting_link?: string;
  rsvp_summary?: CohortEventRsvpSummary;
}

export interface SendCohortEventInvitesRequest {
  video_link?: string;
  scheduled_start?: string;
  scheduled_end?: string;
  timezone?: string;
  coach_ids?: string[];
  participant_ids?: string[];
  additional_emails?: string[];
}

export interface SendCohortEventInvitesResponse {
  event_id: string;
  invitations_created: number;
  calendar_event_created: boolean;
  errors?: string[];
}

export interface CohortEventRsvpDetail {
  id: string;
  recipient_name: string | null;
  email: string;
  recipient_type: EventRecipientType;
  rsvp_status: string;
  rsvp_updated_at: string | null;
}

export interface CohortEventRsvpListResponse {
  event_id: string;
  event_name: string;
  summary: CohortEventRsvpSummary;
  invitations: CohortEventRsvpDetail[];
}

// MED-1349: the CM attendance roster
export type CohortEventRosterRole = 'coach' | 'participant';

export type CohortEventAttendanceStatus = 'attended' | 'partial' | 'absent';

export interface CohortEventRosterEntry {
  user_id: string;
  /** Null when the person has no Medley email. Attendance cannot be recorded. */
  email: string | null;
  name: string | null;
  role: CohortEventRosterRole;
  current_status: string;
  current_absent_reason: AbsentReason | null;
  current_notes: string | null;
  invitation_id: string | null;
  attendance_id: string | null;
  /** False for a withdrawn participant and for a person with no Medley email. */
  is_required: boolean;
  /** MED-1349: a participant who withdrew from the cohort. Listed last, never required. */
  is_withdrawn: boolean;
  is_paid: boolean;
  pay_amount_cents: number | null;
}

export interface CohortEventRosterResponse {
  event_id: string;
  event_name: string;
  event_type: CohortEventType;
  includes_participants: boolean;
  attendees: CohortEventRosterEntry[];
}

export interface CohortEventAttendanceInput {
  email: string;
  status: CohortEventAttendanceStatus;
  absent_reason: AbsentReason | null;
  notes: string | null;
}

export interface MarkCohortEventAttendanceResponse {
  event_id: string;
  attendance_count: number;
  attended_count: number;
  partial_count: number;
  absent_count: number;
}

// Notes (MED-642)
export interface CohortEventNoteWithAuthor {
  id: string;
  event_id: string;
  content: string;
  created_by: string;
  author_name: string;
  author_email: string;
  created_at: string;
}

// Resources (MED-642)
export interface CohortEventResourceWithDetails {
  id: string;
  title: string;
  description: string | null;
  resource_type: 'file' | 'link';
  url: string | null;
  download_url: string | null;
  file_size: number | null;
  original_filename: string | null;
  category_name: string | null;
  access_coaches: boolean;
  access_participants: boolean;
  access_partners: boolean;
  is_evergreen: boolean;
  created_at: string;
}

// ============================================================================
// API Methods
// ============================================================================

export const cohortEventsApi = {
  /**
   * List all cohort events for a cohort
   */
  list: async (cohortId: string): Promise<CohortEventWithDetails[]> => {
    return apiClient.get<CohortEventWithDetails[]>(`/api/cohorts/${cohortId}/events`);
  },

  /**
   * Create a new coach huddle event
   */
  createHuddle: async (
    cohortId: string,
    scheduledStart: string | null,
  ): Promise<CohortEventWithDetails> => {
    return apiClient.post<CohortEventWithDetails>(`/api/cohorts/${cohortId}/events/huddle`, {
      scheduled_start: scheduledStart,
    });
  },

  /**
   * Get a single cohort event by ID
   */
  getById: async (cohortId: string, eventId: string): Promise<CohortEventWithDetails> => {
    return apiClient.get<CohortEventWithDetails>(`/api/cohorts/${cohortId}/events/${eventId}`);
  },

  /**
   * Update event date/time
   */
  updateDateTime: async (
    cohortId: string,
    eventId: string,
    data: { scheduled_start: string; scheduled_end?: string; timezone?: string },
  ): Promise<CohortEventWithDetails> => {
    return apiClient.put<CohortEventWithDetails>(
      `/api/cohorts/${cohortId}/events/${eventId}/datetime`,
      data,
    );
  },

  /**
   * Send calendar invitations
   */
  sendInvites: async (
    cohortId: string,
    eventId: string,
    data: SendCohortEventInvitesRequest,
  ): Promise<SendCohortEventInvitesResponse> => {
    return apiClient.post<SendCohortEventInvitesResponse>(
      `/api/cohorts/${cohortId}/events/${eventId}/invitations`,
      data,
    );
  },

  /**
   * Get RSVP list for an event
   */
  getRsvps: async (cohortId: string, eventId: string): Promise<CohortEventRsvpListResponse> => {
    return apiClient.get<CohortEventRsvpListResponse>(
      `/api/cohorts/${cohortId}/events/${eventId}/invitations`,
    );
  },

  /**
   * Get RSVP summary for an event
   */
  getRsvpSummary: async (cohortId: string, eventId: string): Promise<CohortEventRsvpSummary> => {
    return apiClient.get<CohortEventRsvpSummary>(
      `/api/cohorts/${cohortId}/events/${eventId}/invitations/summary`,
    );
  },

  /**
   * Resend invites (add new recipients)
   */
  resendInvites: async (
    cohortId: string,
    eventId: string,
    data: SendCohortEventInvitesRequest,
  ): Promise<SendCohortEventInvitesResponse> => {
    return apiClient.post<SendCohortEventInvitesResponse>(
      `/api/cohorts/${cohortId}/events/${eventId}/resend-invites`,
      data,
    );
  },

  /**
   * MED-1349: the CM attendance roster (coaches, plus participants for the
   * participant event types) with saved values.
   */
  getAttendanceRoster: async (
    cohortId: string,
    eventId: string,
  ): Promise<CohortEventRosterResponse> => {
    return apiClient.get<CohortEventRosterResponse>(
      `/api/cohorts/${cohortId}/events/${eventId}/attendees`,
    );
  },

  /**
   * MED-1349: save attendance for every required person on the roster.
   */
  saveAttendance: async (
    cohortId: string,
    eventId: string,
    attendance: CohortEventAttendanceInput[],
    // MED-1349: people whose saved status the CM cleared. Only their saved
    // rows are removed; a person left out of attendance keeps the row.
    clearedUserIds: string[] = [],
  ): Promise<MarkCohortEventAttendanceResponse> => {
    return apiClient.post<MarkCohortEventAttendanceResponse>(
      `/api/cohorts/${cohortId}/events/${eventId}/attendance`,
      { attendance, cleared_user_ids: clearedUserIds },
    );
  },

  /**
   * Get cohort timeline for availability request
   */
  getTimeline: async (cohortId: string): Promise<CohortTimelineResponse> => {
    return apiClient.get<CohortTimelineResponse>(`/api/cohorts/${cohortId}/timeline`);
  },

  // ── Notes (MED-642) ──────────────────────────────────────────────────────

  /**
   * Get internal notes for a cohort event
   */
  getNotes: async (
    cohortId: string,
    eventId: string,
  ): Promise<{ notes: CohortEventNoteWithAuthor[] }> => {
    return apiClient.get<{ notes: CohortEventNoteWithAuthor[] }>(
      `/api/cohorts/${cohortId}/events/${eventId}/notes`,
    );
  },

  /**
   * Create an internal note for a cohort event
   */
  createNote: async (
    cohortId: string,
    eventId: string,
    content: string,
  ): Promise<CohortEventNoteWithAuthor> => {
    return apiClient.post<CohortEventNoteWithAuthor>(
      `/api/cohorts/${cohortId}/events/${eventId}/notes`,
      { content },
    );
  },

  // ── Resources (MED-642) ──────────────────────────────────────────────────

  /**
   * Get resources for a cohort event
   */
  getResources: async (
    cohortId: string,
    eventId: string,
  ): Promise<{ data: CohortEventResourceWithDetails[] }> => {
    return apiClient.get<{ data: CohortEventResourceWithDetails[] }>(
      `/api/cohorts/${cohortId}/events/${eventId}/resources`,
    );
  },

  /**
   * Link a resource to a cohort event
   */
  addResource: async (
    cohortId: string,
    eventId: string,
    resourceId: string,
  ): Promise<void> => {
    await apiClient.post(`/api/cohorts/${cohortId}/events/${eventId}/resources`, {
      resource_id: resourceId,
    });
  },

  /**
   * Remove a resource from a cohort event
   */
  removeResource: async (
    cohortId: string,
    eventId: string,
    resourceId: string,
  ): Promise<void> => {
    await apiClient.delete(`/api/cohorts/${cohortId}/events/${eventId}/resources/${resourceId}`);
  },
};

export default cohortEventsApi;
